import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, Building2 as CompaniesLogoIcon } from 'lucide-react';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const validationSchema = Yup.object({
  sigla: Yup.string().required('Sigla è obbligatoria'),
  ragione_sociale: Yup.string().required('Ragione Sociale è obbligatoria'),
  descrizione: Yup.string(),
  cod_fis_piva: Yup.string().required('Codice Fiscale/P.IVA è obbligatorio'),
  stato: Yup.string().required('Stato è obbligatorio'),
  tipo: Yup.string().required('Tipo è obbligatorio'),
  gest_impresa_prof: Yup.string(),
  gestione_fiscale: Yup.string(),
  primo_anno_gest: Yup.number().integer(),
  ult_anno_gest: Yup.number().integer(),
  raggruppamento: Yup.string(),
  livello_azienda: Yup.number().integer(),
});

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const { addJob } = useJob();

  const fetchCompanies = React.useCallback(async () => {
    try {
      const res = await api.get('/companies', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          sort: orderBy,
          order: order.toUpperCase(),
        },
      });
      setCompanies(res.data.companies);
      setTotalItems(res.data.totalItems);
    } catch (error) {
      console.error(error);
      toast.error('Errore nel caricamento delle aziende');
    }
  }, [page, rowsPerPage, search, orderBy, order]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = companies.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handlePrintPdf = async () => {
    try {
      // Start job
      const startRes = await api.post('/companies/report', { ids: selected });
      const jobId = startRes.data.jobId;

      // Delegate monitoring to global JobContext
      addJob(jobId);
      
      // Clear selection after starting job (optional, but good UX)
      setSelected([]);
      
    } catch (error) {
      console.error('Error starting report job:', error);
      toast.error('Errore nell\'avvio della generazione del PDF');
    }
  };

  const formik = useFormik({
    initialValues: {
      sigla: '',
      ragione_sociale: '',
      descrizione: '',
      cod_fis_piva: '',
      stato: '',
      tipo: '',
      gest_impresa_prof: '',
      gestione_fiscale: '',
      primo_anno_gest: new Date().getFullYear(),
      ult_anno_gest: new Date().getFullYear(),
      raggruppamento: '',
      livello_azienda: 1,
      sincronizza: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        if (currentId) {
          await api.put(`/companies/${currentId}`, values);
          toast.success('Azienda aggiornata con successo');
        } else {
          await api.post('/companies', values);
          toast.success('Azienda creata con successo');
        }
        handleClose();
        fetchCompanies();
      } catch (error) {
        console.error(error);
        const data = error.response?.data;
        const validationMessage = Array.isArray(data?.errors)
          ? data.errors.map((item) => item?.msg).filter(Boolean).join(' - ')
          : '';
        const message = data?.message || validationMessage || error.message;
        toast.error(message || 'Errore nel salvataggio dell\'azienda');
      }
    },
  });

  const handleOpen = (company = null) => {
    if (company) {
      setCurrentId(company.id);
      formik.setValues({
        sigla: company.sigla,
        ragione_sociale: company.ragione_sociale,
        descrizione: company.descrizione || '',
        cod_fis_piva: company.cod_fis_piva,
        stato: company.stato,
        tipo: company.tipo,
        gest_impresa_prof: company.gest_impresa_prof || '',
        gestione_fiscale: company.gestione_fiscale || '',
        primo_anno_gest: company.primo_anno_gest || new Date().getFullYear(),
        ult_anno_gest: company.ult_anno_gest || new Date().getFullYear(),
        raggruppamento: company.raggruppamento || '',
        livello_azienda: company.livello_azienda || 1,
        sincronizza: Number(company.sincronizza) !== 0,
      });
    } else {
      setCurrentId(null);
      formik.resetForm();
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questa azienda?')) {
      try {
        await api.delete(`/companies/${id}`);
        toast.success('Azienda eliminata con successo');
        fetchCompanies();
      } catch (error) {
        console.error(error);
        toast.error('Errore nella cancellazione dell\'azienda');
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSyncPasspartout = async () => {
    try {
      setSyncLoading(true);
      const res = await api.post('/companies/sync-passpartout');
      toast.success(
        res.data?.message ||
          'Sincronizzazione aziende completata'
      );
      fetchCompanies();
    } catch (error) {
      console.error('Errore sincronizzazione aziende:', error);
      const data = error.response?.data;
      const baseMessage =
        data?.message ||
        'Errore durante la sincronizzazione con Passpartout';
      const extra = [data?.status, data?.details].filter(Boolean).join(' - ');
      toast.error(extra ? `${baseMessage}: ${extra}` : baseMessage);
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CompaniesLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Aziende
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione anagrafica aziende e sincronizzazione Passpartout
            </Typography>
          </Box>
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleSyncPasspartout}
            disabled={syncLoading}
            sx={{
              mr: 2,
              textTransform: 'none',
            }}
          >
            {syncLoading ? 'Sincronizzo...' : 'Sincronizza'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={handlePrintPdf}
            disabled={selected.length === 0}
            sx={{ 
              mr: 2,
              textTransform: 'none',
            }}
          >
            Stampa PDF
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{ 
              textTransform: 'none',
            }}
          >
            Nuova Azienda
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Cerca azienda..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 500 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.neutral' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < companies.length}
                    checked={companies.length > 0 && selected.length === companies.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'sigla'}
                    direction={orderBy === 'sigla' ? order : 'asc'}
                    onClick={() => handleRequestSort('sigla')}
                  >
                    Sigla
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'ragione_sociale'}
                    direction={orderBy === 'ragione_sociale' ? order : 'asc'}
                    onClick={() => handleRequestSort('ragione_sociale')}
                  >
                    Ragione Sociale
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'cod_fis_piva'}
                    direction={orderBy === 'cod_fis_piva' ? order : 'asc'}
                    onClick={() => handleRequestSort('cod_fis_piva')}
                  >
                    P.IVA / Cod. Fisc.
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'stato'}
                    direction={orderBy === 'stato' ? order : 'asc'}
                    onClick={() => handleRequestSort('stato')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'tipo'}
                    direction={orderBy === 'tipo' ? order : 'asc'}
                    onClick={() => handleRequestSort('tipo')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'primo_anno_gest'}
                    direction={orderBy === 'primo_anno_gest' ? order : 'asc'}
                    onClick={() => handleRequestSort('primo_anno_gest')}
                  >
                    Primo anno
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'sincronizza'}
                    direction={orderBy === 'sincronizza' ? order : 'asc'}
                    onClick={() => handleRequestSort('sincronizza')}
                  >
                    Sincronizza
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'ult_anno_gest'}
                    direction={orderBy === 'ult_anno_gest' ? order : 'asc'}
                    onClick={() => handleRequestSort('ult_anno_gest')}
                  >
                    Ultimo anno
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((company) => {
                const isItemSelected = selected.indexOf(company.id) !== -1;
                return (
                  <TableRow 
                    key={company.id} 
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(event) => handleClick(event, company.id)}
                      />
                    </TableCell>
                    <TableCell>{company.sigla}</TableCell>
                  <TableCell sx={{ fontWeight: 'medium' }}>{company.ragione_sociale}</TableCell>
                    <TableCell>{company.cod_fis_piva}</TableCell>
                    <TableCell>{company.stato}</TableCell>
                    <TableCell>{company.tipo}</TableCell>
                    <TableCell align="center">
                      {company.primo_anno_gest || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox checked={Number(company.sincronizza) !== 0} disabled />
                    </TableCell>
                    <TableCell align="center">
                      {company.ult_anno_gest || '-'}
                    </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(company)} color="primary">
                      <EditIcon size={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(company.id)} color="error">
                      <DeleteIcon size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
              {companies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      Nessuna azienda trovata
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </StyledCard>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography variant="h6" component="h2" mb={3}>
            {currentId ? 'Modifica Azienda' : 'Nuova Azienda'}
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="sigla"
                  name="sigla"
                  label="Sigla"
                  value={formik.values.sigla}
                  onChange={formik.handleChange}
                  error={formik.touched.sigla && Boolean(formik.errors.sigla)}
                  helperText={formik.touched.sigla && formik.errors.sigla}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="ragione_sociale"
                  name="ragione_sociale"
                  label="Ragione Sociale"
                  value={formik.values.ragione_sociale}
                  onChange={formik.handleChange}
                  error={formik.touched.ragione_sociale && Boolean(formik.errors.ragione_sociale)}
                  helperText={formik.touched.ragione_sociale && formik.errors.ragione_sociale}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="descrizione"
                  name="descrizione"
                  label="Descrizione"
                  multiline
                  rows={2}
                  value={formik.values.descrizione}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="cod_fis_piva"
                  name="cod_fis_piva"
                  label="Codice Fiscale / P.IVA"
                  value={formik.values.cod_fis_piva}
                  onChange={formik.handleChange}
                  error={formik.touched.cod_fis_piva && Boolean(formik.errors.cod_fis_piva)}
                  helperText={formik.touched.cod_fis_piva && formik.errors.cod_fis_piva}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="stato"
                  name="stato"
                  label="Stato"
                  value={formik.values.stato}
                  onChange={formik.handleChange}
                  error={formik.touched.stato && Boolean(formik.errors.stato)}
                  helperText={formik.touched.stato && formik.errors.stato}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="tipo"
                  name="tipo"
                  label="Tipo"
                  value={formik.values.tipo}
                  onChange={formik.handleChange}
                  error={formik.touched.tipo && Boolean(formik.errors.tipo)}
                  helperText={formik.touched.tipo && formik.errors.tipo}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="gest_impresa_prof"
                  name="gest_impresa_prof"
                  label="Gest. Impresa Prof."
                  value={formik.values.gest_impresa_prof}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="gestione_fiscale"
                  name="gestione_fiscale"
                  label="Gestione Fiscale"
                  value={formik.values.gestione_fiscale}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="raggruppamento"
                  name="raggruppamento"
                  label="Raggruppamento"
                  value={formik.values.raggruppamento}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  id="primo_anno_gest"
                  name="primo_anno_gest"
                  label="Primo Anno Gest."
                  type="number"
                  value={formik.values.primo_anno_gest}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  id="ult_anno_gest"
                  name="ult_anno_gest"
                  label="Ultimo Anno Gest."
                  type="number"
                  value={formik.values.ult_anno_gest}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  id="livello_azienda"
                  name="livello_azienda"
                  label="Livello Azienda"
                  type="number"
                  value={formik.values.livello_azienda}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sincronizza"
                      checked={Boolean(formik.values.sincronizza)}
                      onChange={(event) => {
                        formik.setFieldValue('sincronizza', event.target.checked);
                      }}
                    />
                  }
                  label="Sincronizza azienda"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={handleClose} variant="outlined">
                Annulla
              </Button>
              <Button type="submit" variant="contained">
                {currentId ? 'Aggiorna' : 'Crea'}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </Box>
  );
}
